{#if performSalePossible}
    <button class="btn btn-primary" on:click={() => selling ? () => {} : performSale()}>
      { $_("unseated.to_cart") } &nbsp;<i class="d-none d-sm-inline-block fas fa-chevron-right"></i>
    </button>
{:else}
    <Popover title={$_("unseated.to_cart_disabled._header")} content={`<ul>${unsellableReasons.map(s => `<li>${s}</li>`).join("")}<ul>`} trigger="hover" html={true}>
        <button class="btn btn-primary btn-disabled" disabled>
          { $_("unseated.to_cart") } &nbsp;<i class="d-none d-sm-inline-block fas fa-chevron-right"></i>
        </button>
    </Popover>
{/if}

<style lang="less">
    button {
        width: 100%;
    }
</style>


<script lang="ts">
    import { _ } from "@frontend/translations";
    import { derived, readable } from "svelte/store";
    import { catMap, getForms, getCoupons } from "./unseated";
    import { depromise } from "@/utils/svelte/proxies";
    import { Category, Discount, EventPriceInformation, Shop } from "@ticketmachine/sdk";
    import Spinner from "tm:widgets/spinner";
    import { custom_setting } from "@/frontend/base/state";
    import Popover from "@/components/Popover.svelte";
    import { components } from "@/frontend/base/state";

    const event_ids = derived(catMap, (c) => Array.from(new Set(Object.keys(c).map(k => k.split("-")[0]).map((n) => Number(n)))));
    const events = depromise(null, derived(event_ids, (eids) => Promise.all(eids.map(e => Shop.current.getEventPrices(e)))));

    const loading = components.loading;

    function calculateFullPrice(events: EventPriceInformation[]|null, cats: Record<string, number>): number|null {
        if (events == null) return null;
        let price = 0;
        for (let [k, v] of Object.entries(cats)) {
            const items = k.split("-").map(n => Number(n));
            const ev = events.find((e) => e.id === items[0]);
            if (ev === null || ev === undefined) return null;

            const cat = ev.categories.find((c) => c.id === items[1]);
            if (cat === null || cat === undefined) return null;
            if (items.length == 2) {
                price += cat.price * v;
                continue;
            }

            const disc = cat.discounts.find((d) => d.id === items[2]);
            if (disc === null || disc === undefined) return null;
            price += disc.price * v;
        }
        return price;
    }

    $: fullPrice = calculateFullPrice($events, $catMap);
    $: ticketCount = Object.values($catMap).reduce((p, n) => n+p, 0);
    const _minimumTicketCount = depromise(1, readable(custom_setting<number>("min_sale_count")));
    $: minimumTicketCount = Math.max(1, $_minimumTicketCount);
    let selling = false;

    function dataIf(pred: boolean, text: string): string|null {
        return pred ? text : null;
    }

    $: unsellableReasons = [
        dataIf((ticketCount < minimumTicketCount), $_("unseated.to_cart_disabled.not_enough_tickets", [ticketCount, minimumTicketCount], ["current", "min"])),
        dataIf(selling, $_("unseated.to_cart_disabled.already_selling")),
        dataIf(fullPrice === null, $_("unseated.to_cart_disabled.loading")),
        dataIf($loading > 0, $_("unseated.to_cart_disabled.page_is_loading"))
    ].filter(f => !!f);

    $: performSalePossible = unsellableReasons.length === 0;

    export function getTicketObject(evs: EventPriceInformation[], k: string): Discount|Category {
        const items = k.split("-").map(n => Number(n));
        const ev = evs.find((e) => e.id === items[0]);
        if (ev === null || ev === undefined) throw new Error("Failed to find discount");
    
        const cat = ev.categories.find((c) => c.id === items[1]);
        if (cat === null || cat === undefined) throw new Error("Failed to find discount");
        if (items.length == 2) {
            return cat;
        }
    
        const disc = cat.discounts.find((d) => d.id === items[2]);
        if (disc === null || disc === undefined) throw new Error("Failed to find discount.");
    
        return disc;
    }

    const COUPON_REGEX = /^\/((?:\\\/|[^\/])+)\/((?:\\\/|[^\/])*)\/([gimsuy]*)$/;
    function applyCouponRegex(regex: string, code: string|null): string|null {
        if (!code) return code;
        if (regex === "") return code;
        const match = COUPON_REGEX.exec(regex);
        if (!match) return code;

        const substituter = new RegExp(match[1], match[3]);
        return code.replace(substituter, match[2]);
    }

    function performSale() {
        const forms = getForms();
        if (forms === null) return;

        const coupons = getCoupons();
        if (coupons === null) return;

        Spinner.run(async () => {
            const coupon_regex = await custom_setting<string>("coupon_regex")
            try {
                selling = true;

                await Shop.current.runTransaction(async ({cart}) => {
                    for (let [k, v] of Object.entries($catMap)) {
                        const addable = getTicketObject($events!, k);

                        for (let i of Array.from(new Array(v)).map((_, i) => i)) {
                            const formKey = `${k}:${i}`;
                            const form = forms[formKey];
                            let coupon = coupons[formKey];
                            if (!!coupon) coupon = applyCouponRegex(coupon_regex||"", coupon);
                            await cart.add(addable, 1, {form, coupon});
                        }
                    }
                    await cart.onComplete(() => {
                        window.location.href = "/checkout";
                    });
                });

                $catMap = {};
            } finally {
                selling = false;
            }
        }).catch(window.reportError);
    }
</script>
